import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { 
  MdPeople, 
  MdAccountBalance, 
  MdReceipt, 
  MdPending,
  MdBusiness,
  MdCalendarToday,
  MdCurrencyExchange,
  MdWorkspacePremium,
  MdAdd,
  MdBook,
  MdInsertChart,
  MdSettings,
  MdTrendingUp
} from 'react-icons/md';
import { useAuth } from '../../context/AuthContext';
import { useTenant } from '../../context/TenantContext';
import tenantService from '../../services/tenantService';
import { formatDate } from '../../utils/helpers';
import { OrganizationStats } from '../../types';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { organization, loading: orgLoading } = useTenant();
  const navigate = useNavigate();
  const [stats, setStats] = useState<OrganizationStats | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    loadStats();
  }, [organization]);

  const loadStats = async (): Promise<void> => {
    if (!organization) return;

    try {
      setLoading(true);
      const data = await tenantService.getOrganizationStats();
      setStats(data);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  // Show loading while fetching organizations
  if (orgLoading) {
    return (
      <Container>
        <LoadingState>
          <LoadingSpinner />
          <p>Loading your organizations...</p>
        </LoadingState>
      </Container>
    );
  }

  // Show empty state only if loading is complete and no organization exists
  if (!organization) {
    return (
      <Container>
        <NoOrgMessage>
          <MdBusiness style={{ fontSize: '64px', color: '#D1D5DB', marginBottom: '16px' }} />
          <h2>No Organization Selected</h2>
          <p>Please create or select an organization to continue.</p>
        </NoOrgMessage>
      </Container>
    );
  }

  return (
    <Container>
      <WelcomeSection>
        <WelcomeContent>
          <WelcomeTitle>Welcome back, {user?.full_name?.split(' ')[0]}!</WelcomeTitle>
          <OrganizationInfo>
            <MdBusiness />
            <span>{organization.org_name}</span>
          </OrganizationInfo>
        </WelcomeContent>
        <QuickActionButtons>
          <QuickActionButton onClick={() => navigate('/accounting/journal-entry')} $color="#1e3a8a">
            <MdAdd />
            <span>New Entry</span>
          </QuickActionButton>
          <QuickActionButton onClick={() => navigate('/accounting/reports')} $color="#059669">
            <MdInsertChart />
            <span>Reports</span>
          </QuickActionButton>
        </QuickActionButtons>
      </WelcomeSection>

      {loading ? (
        <LoadingState>
          <LoadingSpinner />
          <p>Loading dashboard...</p>
        </LoadingState>
      ) : (
        <>
          <StatsGrid>
            <StatCard>
              <StatIconContainer $bg="linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)">
                <MdPeople />
              </StatIconContainer>
              <StatContent>
                <StatLabel>Active Users</StatLabel>
                <StatValue>{stats?.userCount || 0}</StatValue>
                <StatTrend $positive={true}>
                  <MdTrendingUp />
                  <span>All time</span>
                </StatTrend>
              </StatContent>
            </StatCard>

            <StatCard>
              <StatIconContainer $bg="linear-gradient(135deg, #059669 0%, #10b981 100%)">
                <MdAccountBalance />
              </StatIconContainer>
              <StatContent>
                <StatLabel>Chart of Accounts</StatLabel>
                <StatValue>{stats?.accountCount || 0}</StatValue>
                <StatTrend $positive={true}>
                  <MdBook />
                  <span>Active accounts</span>
                </StatTrend>
              </StatContent>
            </StatCard>

            <StatCard>
              <StatIconContainer $bg="linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)">
                <MdReceipt />
              </StatIconContainer>
              <StatContent>
                <StatLabel>Journal Entries</StatLabel>
                <StatValue>{stats?.journalEntryCount || 0}</StatValue>
                <StatTrend $positive={true}>
                  <MdInsertChart />
                  <span>Posted entries</span>
                </StatTrend>
              </StatContent>
            </StatCard>

            <StatCard>
              <StatIconContainer $bg="linear-gradient(135deg, #dc2626 0%, #f87171 100%)">
                <MdPending />
              </StatIconContainer>
              <StatContent>
                <StatLabel>Pending</StatLabel>
                <StatValue>{stats?.pendingEntries || 0}</StatValue>
                <StatTrend $positive={false}>
                  <MdPending />
                  <span>Awaiting review</span>
                </StatTrend>
              </StatContent>
            </StatCard>
          </StatsGrid>

          <ContentGrid>
            <OrgDetailsCard>
              <CardHeader>
                <CardTitle>
                  <MdBusiness />
                  <span>Organization Details</span>
                </CardTitle>
              </CardHeader>
              <CardBody>
                <DetailRow>
                  <DetailLabel>
                    <MdBusiness />
                    <span>Organization Name</span>
                  </DetailLabel>
                  <DetailValue>{organization.org_name}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>
                    <MdCurrencyExchange />
                    <span>Base Currency</span>
                  </DetailLabel>
                  <DetailValue>{organization.base_currency}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>
                    <MdCalendarToday />
                    <span>Created</span>
                  </DetailLabel>
                  <DetailValue>{formatDate(organization.created_at)}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>
                    <MdWorkspacePremium />
                    <span>Subscription</span>
                  </DetailLabel>
                  <SubscriptionBadge>{organization.subscription_tier || 'Trial'}</SubscriptionBadge>
                </DetailRow>
              </CardBody>
            </OrgDetailsCard>

            <QuickActionsCard>
              <CardHeader>
                <CardTitle>
                  <MdSettings />
                  <span>Quick Actions</span>
                </CardTitle>
              </CardHeader>
              <CardBody>
                <ActionsList>
                  <ActionItem onClick={() => navigate('/finance-health')}>
                    <ActionIcon $color="#DC2626">
                      <MdTrendingUp />
                    </ActionIcon>
                    <ActionContent>
                      <ActionTitle>Finance Health</ActionTitle>
                      <ActionDescription>Real-time financial insights</ActionDescription>
                    </ActionContent>
                  </ActionItem>
                  <ActionItem onClick={() => navigate('/accounting/journal-entry')}>
                    <ActionIcon $color="#1e3a8a">
                      <MdAdd />
                    </ActionIcon>
                    <ActionContent>
                      <ActionTitle>New Journal Entry</ActionTitle>
                      <ActionDescription>Record a new transaction</ActionDescription>
                    </ActionContent>
                  </ActionItem>
                  <ActionItem onClick={() => navigate('/accounting/chart-of-accounts')}>
                    <ActionIcon $color="#059669">
                      <MdAccountBalance />
                    </ActionIcon>
                    <ActionContent>
                      <ActionTitle>Chart of Accounts</ActionTitle>
                      <ActionDescription>View all accounts</ActionDescription>
                    </ActionContent>
                  </ActionItem>
                  <ActionItem onClick={() => navigate('/accounting/reports')}>
                    <ActionIcon $color="#7c3aed">
                      <MdInsertChart />
                    </ActionIcon>
                    <ActionContent>
                      <ActionTitle>Financial Reports</ActionTitle>
                      <ActionDescription>P&L and Balance Sheet</ActionDescription>
                    </ActionContent>
                  </ActionItem>
                </ActionsList>
              </CardBody>
            </QuickActionsCard>
          </ContentGrid>
        </>
      )}
    </Container>
  );
};

const Container = styled.div`
  padding: 32px;
  max-width: 1600px;
  margin: 0 auto;
  background: #F8F9FA;
  min-height: calc(100vh - 64px);

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const WelcomeSection = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  margin-bottom: 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
  }
`;

const WelcomeContent = styled.div`
  flex: 1;
`;

const WelcomeTitle = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 12px 0;
  letter-spacing: -0.5px;
`;

const OrganizationInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #6B7280;
  font-size: 16px;

  svg {
    font-size: 20px;
  }
`;

const QuickActionButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const QuickActionButton = styled.button<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: ${props => props.$color};
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  svg {
    font-size: 20px;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15);
  }
`;

const NoOrgMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 100px 20px;
  text-align: center;

  h2 {
    font-size: 24px;
    color: #111827;
    margin: 0 0 8px 0;
  }

  p {
    color: #6B7280;
    font-size: 15px;
  }
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 100px 20px;
  gap: 16px;

  p {
    color: #6B7280;
    font-size: 15px;
  }
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #E5E7EB;
  border-top-color: #1e3a8a;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
`;

const StatCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  gap: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  transition: all 0.2s;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  }
`;

const StatIconContainer = styled.div<{ $bg: string }>`
  width: 64px;
  height: 64px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.$bg};
  color: white;
  flex-shrink: 0;

  svg {
    font-size: 32px;
  }
`;

const StatContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const StatLabel = styled.div`
  font-size: 13px;
  color: #6B7280;
  font-weight: 500;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatValue = styled.div`
  font-size: 36px;
  font-weight: 700;
  color: #111827;
  line-height: 1;
  margin-bottom: 8px;
`;

const StatTrend = styled.div<{ $positive: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: ${props => props.$positive ? '#059669' : '#DC2626'};
  font-weight: 500;

  svg {
    font-size: 16px;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 24px;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const OrgDetailsCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  overflow: hidden;
`;

const QuickActionsCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  overflow: hidden;
`;

const CardHeader = styled.div`
  padding: 24px;
  border-bottom: 1px solid #E5E7EB;
`;

const CardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 18px;
  font-weight: 600;
  color: #111827;

  svg {
    font-size: 24px;
    color: #1e3a8a;
  }
`;

const CardBody = styled.div`
  padding: 24px;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid #F3F4F6;

  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #6B7280;
  font-size: 14px;
  font-weight: 500;

  svg {
    font-size: 18px;
  }
`;

const DetailValue = styled.div`
  color: #111827;
  font-weight: 600;
  font-size: 14px;
`;

const SubscriptionBadge = styled.span`
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ActionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ActionItem = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: #F9FAFB;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #F3F4F6;
    transform: translateX(4px);
  }
`;

const ActionIcon = styled.div<{ $color: string }>`
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background: ${props => props.$color};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    font-size: 24px;
  }
`;

const ActionContent = styled.div`
  flex: 1;
`;

const ActionTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 2px;
`;

const ActionDescription = styled.div`
  font-size: 12px;
  color: #6B7280;
`;

export default Dashboard;
